/**
 * Reading a value out of tool arguments that came off the log.
 *
 * A tool call's arguments are stored by `codec.encodeJson`, which wraps every
 * inner string in `Automerge.ImmutableString` to match how automorph stores a
 * `serde_json::Value`. Nothing decodes them on the way back: `projectTranscript`
 * hands `b.arguments` straight through, so what a renderer receives is an object
 * graph whose leaves are `{ val: string }` wrappers rather than strings.
 *
 * `typeof value === 'string'` is therefore false for every string argument of
 * every tool call in the app, while being true for every one in a test that
 * builds its arguments by hand. That gap is why this went unnoticed: it cannot
 * fail in a unit test, and it fails everywhere else.
 *
 * `JSON.stringify` hides it (`ImmutableString` serialises as its text), which is
 * why the raw arguments block has always rendered correctly and every reader
 * that inspects a FIELD has silently found nothing.
 */

/**
 * The text of an argument, whether it arrived as a string or as the wrapper the
 * log stores. `undefined` when the value is not textual at all.
 *
 * Duck-typed on `val` rather than `instanceof ImmutableString`: pulling
 * `@automerge/automerge` into a render path to identify a one-field wrapper is a
 * large dependency for a small question, and a value that reaches here from a
 * different automerge instance than the one bundled would fail `instanceof`
 * while being exactly the thing we are looking for.
 */
export function argString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (value !== null && typeof value === 'object') {
    const inner = (value as { val?: unknown }).val
    if (typeof inner === 'string') return inner
  }
  return undefined
}
